/// <amd-module name="Artifacts/Feed/components/FeedHub" />

import * as Res from "Artifacts/Feed/Resources";
import { ArtifactsSettingsPanel } from "Artifacts/Feed/components/ArtifactsSettingsPanel";
import { DeletedFeed } from "Artifacts/Feed/components/DeletedFeed";
import {
  FeedHubState,
  WhenEmptyFeed,
  WhenFeedIsDeleted,
  WhenFeedWithPackagesOrFiltered,
  WhenProblemPackagesExist,
} from "Artifacts/Feed/components/FeedHubState";
import { NoPackages, NoPackagesFiltered } from "Artifacts/Feed/components/NoPackages";
import {
  IPackageListFilterBarProps,
  PackageListFilterBar,
} from "Artifacts/Feed/components/PackageListFilterBar";
import { PackageListTable } from "Artifacts/Feed/components/PackageListTable";
import { UnavailablePackageListTable } from "Artifacts/Feed/components/UnavailablePackageListTable";
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { FeedPickerContainer } from "Artifacts/FeedPicker/containers/FeedPickerContainer";
import { ComponentState } from "Artifacts/Root/Constants";
import * as RootRes from "Artifacts/Root/Resources";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { getFeedIdentifier, isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import {
  isUserAnonymousOrPublic,
  isUserPermittedToUseCommand,
  userCanCreateFeeds,
} from "Artifacts/Root/helpers/UserHelpers";
import { ICommand } from "Artifacts/Root/protocols/types";
import {
  connectToFeed,
  packageRecycleBinHubRoute,
  settingsRoute,
} from "Artifacts/Root/routing/Constants";
import { IFeed } from "Artifacts/Root/types";
import { PackageListDataItem, ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import { useComponentContext } from "VSS/Platform/Layout";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { IListSelection } from "VSSUI/List";
import { MenuItemType } from "VSSUI/Menu";
import { Page } from "VSSUI/Page";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import * as React from "react";
import { generatePath, useHistory } from "react-router-dom";

export interface IFeedHubProps {
  packageListComponentState: ComponentState;
  problemPackageListComponentState: ComponentState;
  feed?: IFeed;
  feedComponentState: ComponentState;
  view?: ViewIdentifier;
  problemPackageData?: ProblemPackageItem[];
  packageData?: PackageListDataItem[];
  packageListSelection: IListSelection;
  commands: ICommand[];
  filterBarProps: IPackageListFilterBarProps;
  isFilterActive: boolean;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  showCFSUI: boolean;
  showCreateFeedModal: () => void;
  showSearchUpstreamsModal: () => void;
}

export const FeedHub: React.FC<IFeedHubProps> = ({
  packageListComponentState,
  problemPackageListComponentState,
  feedComponentState,
  feed,
  view,
  problemPackageData,
  packageData,
  packageListSelection,
  commands,
  filterBarProps,
  scrollRef,
  isFilterActive,
  showCFSUI,
  showCreateFeedModal,
  showSearchUpstreamsModal,
}) => {
  const { pageContext } = useComponentContext();
  const history = useHistory();
  const [artifactsSettingsPanelOpen, setArtifactsSettingsPanelOpen] = React.useState<boolean>(
    false,
  );

  const isUserAnonymous = isUserAnonymousOrPublic(pageContext);

  const commandBarItems: IHeaderCommandBarItem[] = [
    ...commands.map<IHeaderCommandBarItem>((command) => ({
      id: command.id,
      onActivate: command.action,
      text: command.displayName,
      itemType: MenuItemType.Normal,
      iconProps: command.iconProps,
      disabled:
        (isUserAnonymous && !command.isAnonymousAllowed) ||
        (feed != null && isUserPermittedToUseCommand(command, feed)),
    })),
    {
      id: "connect-to-feed",
      text: RootRes.ConnectToFeedButton,
      iconProps: {
        iconName: "PlugConnected",
      },
      isPrimary: false,
      onActivate: () => {
        feed &&
          history.push(
            generatePath(connectToFeed, {
              feedIdentifier: getFeedIdentifier(feed, view),
            }),
          );
      },
      itemType: MenuItemType.Normal,
      disabled: feed === undefined || feed?.isDeleted,
    },
    {
      id: "create-feed",
      text: Res.CreateFeedButton,
      onActivate: showCreateFeedModal,
      iconProps: {
        iconName: "Add",
      },
      itemType: MenuItemType.Normal,
      disabled: feed === undefined || !userCanCreateFeeds(feed),
    },
  ];

  commandBarItems.push({
    id: "search-upstreams",
    text: RootRes.ShowSearchUpstreamsPanelButtonText,
    iconProps: { iconName: "Search" },
    onActivate: showSearchUpstreamsModal,
  });

  commandBarItems.push(
    {
      id: "recycle-bin",
      text: Res.RecycleBinButton,
      onActivate: () =>
        feed &&
        history.push(
          generatePath(packageRecycleBinHubRoute, {
            feedIdentifier: getFeedIdentifier(feed, view),
          }),
        ),
      iconProps: {
        iconName: "Delete",
      },
      itemType: MenuItemType.Normal,
      disabled: feed === undefined || feed?.isDeleted || isUserAnonymous,
    },
    {
      id: "feed-settings",
      ariaLabel: Res.FeedSettingsButton,
      iconProps: {
        iconName: "Settings",
      },
      onActivate: () => {
        feed &&
          history.push(
            generatePath(settingsRoute, { feedIdentifier: getFeedIdentifier(feed, view) }),
          );
      },
      itemType: MenuItemType.Normal,
      disabled: feed === undefined || isUserAnonymous,
      subtle: true,
      important: true,
    },
  );

  if (feed !== undefined && !isUserAnonymous) {
    commandBarItems.push({
      id: "azure-artifacts-settings",
      ariaLabel: Res.ArtifactsSettingsButton,
      iconProps: {
        iconName: "PeopleSettings",
      },
      onActivate: () => {
        setArtifactsSettingsPanelOpen(!artifactsSettingsPanelOpen);
      },
      itemType: MenuItemType.Normal,
      subtle: true,
      important: true,
    });
  }

  const [selectedTabId, setSelectedTabId] = React.useState<string>("available");

  return (
    <Page className="feedhub">
      <CustomHeader className="feedhub-header">
        <HeaderTitleArea>
          <div className="flex-row">
            <h1 className="visibility-hidden" /*hidden h1 for a11y*/>{feed?.name}</h1>
            <FeedPickerContainer currentFeed={feed} />
            {feed && isPublicFeed(feed) && (
              <div aria-label={Res.AriaLabel_PublicFeed} className="public-feed">
                <Icon
                  ariaLabel={Res.AriaLabel_GlobeIcon}
                  iconName="Globe"
                  className="public-feed-globe"
                />
                <span>{Res.Public}</span>
              </div>
            )}
          </div>
        </HeaderTitleArea>
        <HeaderCommandBar items={commandBarItems} buttonCount={4} />
      </CustomHeader>
      <DefaultLoader state={feedComponentState}>
        <DefaultLoader state={packageListComponentState}>
          <div className="page-content page-content-top v-scroll-auto" ref={scrollRef}>
            <FeedHubState
              feed={feed}
              packageListComponentState={packageListComponentState}
              problemPackageListComponentState={problemPackageListComponentState}
              packageData={packageData}
              problemPackageData={problemPackageData}
              isFilterActive={isFilterActive}
              showCFSUI={showCFSUI}
            >
              <WhenFeedWithPackagesOrFiltered>
                <PackageListFilterBar {...filterBarProps} />
                <DefaultLoader state={packageListComponentState}>
                  {packageData && packageData.length > 0 ? (
                    <PackageListTable
                      packageData={packageData}
                      selection={packageListSelection}
                      feed={feed}
                      view={view}
                    />
                  ) : (
                    <NoPackagesFiltered showSearchUpstreamsModal={showSearchUpstreamsModal} />
                  )}
                </DefaultLoader>
              </WhenFeedWithPackagesOrFiltered>
              <WhenProblemPackagesExist>
                <TabBar
                  onSelectedTabChanged={setSelectedTabId}
                  selectedTabId={selectedTabId}
                  tabSize={TabSize.Compact}
                  className="tab-bar"
                >
                  <Tab name={Res.PackageListTabs_Available} id={"available"} />
                  <Tab
                    name={Res.PackageListTabs_Unavailable}
                    id={"unavailable"}
                    badgeCount={problemPackageData?.length}
                  />
                </TabBar>
                {selectedTabId === "available" ? (
                  <React.Fragment>
                    <PackageListFilterBar {...filterBarProps} />
                    <DefaultLoader state={packageListComponentState}>
                      {packageData && packageData.length > 0 ? (
                        <PackageListTable
                          packageData={packageData}
                          selection={packageListSelection}
                          feed={feed}
                          view={view}
                        />
                      ) : (
                        <NoPackagesFiltered showSearchUpstreamsModal={showSearchUpstreamsModal} />
                      )}
                    </DefaultLoader>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <DefaultLoader state={problemPackageListComponentState}>
                      {problemPackageData && problemPackageData.length > 0 ? (
                        <UnavailablePackageListTable
                          problemPackageData={problemPackageData}
                          feed={feed}
                          view={view}
                        />
                      ) : (
                        <NoPackagesFiltered showSearchUpstreamsModal={showSearchUpstreamsModal} />
                      )}
                    </DefaultLoader>
                  </React.Fragment>
                )}
              </WhenProblemPackagesExist>
              <WhenEmptyFeed>
                <NoPackages showSearchUpstreamsModal={showSearchUpstreamsModal} />
              </WhenEmptyFeed>
              <WhenFeedIsDeleted>
                <DeletedFeed feed={feed} />
              </WhenFeedIsDeleted>
            </FeedHubState>
          </div>
        </DefaultLoader>
        {artifactsSettingsPanelOpen && (
          <ArtifactsSettingsPanel onDismiss={() => setArtifactsSettingsPanelOpen(false)} />
        )}
      </DefaultLoader>
    </Page>
  );
};
